import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { FormField } from '@hologram-dimension/form-field';
import { Link } from '@hologram-dimension/link';
import { Loader } from '@hologram-dimension/loader';
import { Modal } from '@hologram-dimension/modal';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { TextInputField } from '@hologram-dimension/text-input-field';
import useDevice from 'api/legacy/devices/useDevice';
import usePurchasePhoneNumber from 'api/legacy/devices/usePurchasePhoneNumber';
import usePurchasePhoneNumberPreview from 'api/legacy/devices/usePurchasePhoneNumberPreview';
import useBalance from 'api/legacy/organization/useBalance';
import useBilling from 'api/legacy/organization/useBilling';
import useSimDetails from 'api/sims/useSimDetails';
import { clsx } from 'clsx/lite';
import BillingAddress from 'components/BillingAddress/BillingAddress';
import useCountries from 'hooks/useCountries';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useForm } from 'react-hook-form';
import useLegacyDashboard from 'state/contexts/LegacyDashboard/useLegacyDashboard';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { LegacyDevice } from 'types/legacyDashboard';
import { useDebounceCallback } from 'utils/hooks';
import useOrgUrl from 'utils/legacy/useOrgUrl';
import formatMoney from 'utils/numbers/formatMoney';
import formatNumber from 'utils/numbers/formatNumber';
import styles from './PurchasePhoneNumber.module.css';

interface PurchasePhoneNumberProps {
  hologramSimId?: HologramSimId;
  deviceId?: DeviceId;
  onConfirmed: () => void;
  onCancelled: () => void;
}

interface PurchasePhoneNumberFormFields {
  countryCode: string;
  areaCode: string;
}

// TODO: We will probably need more confirmation dialogs in the future, consider refactoring this into a generic confirmation dialog

const PurchasePhoneNumber: FC<PurchasePhoneNumberProps> = ({
  hologramSimId,
  deviceId,
  onConfirmed,
  onCancelled,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [areaCodeForPreview, setAreaCodeForPreview] = useState<string | null>(null);

  const organizationId = useOrganization();
  const { simDetails, mutate: mutateSimDetails } = useSimDetails(hologramSimId);
  const { device: legacyDeviceDetails, mutate: mutateDevice } = useDevice(deviceId, organizationId);

  // Billing info
  const { billing, isLoading: isBillingLoading, error: billingError } = useBilling(organizationId);
  const { balance, isLoading: isBalanceLoading, error: balanceError } = useBalance(organizationId);

  const { register, watch, setValue } = useForm<PurchasePhoneNumberFormFields>({
    mode: 'onChange',
    defaultValues: { countryCode: billing.billing_address?.country ?? '', areaCode: '' },
  });

  useEffect(() => {
    setValue('countryCode', billing.billing_address?.country ?? '');
  }, [billing, setValue]);

  const { areaCode, countryCode } = watch();

  const updateAreaCodeForPreview = useCallback(() => {
    setAreaCodeForPreview(areaCode);
  }, [areaCode]);
  useDebounceCallback(updateAreaCodeForPreview, 750);

  const {
    phoneNumberCost,
    isLoading: isPhoneNumberCostLoading,
    error: phoneNumberCostError,
  } = usePurchasePhoneNumberPreview({
    organizationId,
    deviceId,
    country: isBillingLoading ? undefined : countryCode,
    areaCode: areaCodeForPreview ?? undefined,
  });

  const isLoading = isBillingLoading || isBalanceLoading;
  const apiError = billingError || balanceError;

  const purchasePhoneNumber = usePurchasePhoneNumber({ deviceId, organizationId });

  const onPurchasePhoneNumber = useCallback(async () => {
    setError(null);
    setProcessing(true);

    try {
      const result = await purchasePhoneNumber(
        countryCode,
        areaCode ? parseInt(areaCode, 10) : undefined
      );
      const newPhone = result?.data ?? '';
      const newPhoneCost = result?.order_data?.unit_cost ?? '0.0000';

      if (result?.success) {
        mutateSimDetails(
          { simDetails: { ...structuredClone(simDetails), phoneNumber: newPhone } },
          { revalidate: false } // We WILL revalidate soon, but with DMS latency it might take a few seconds. Let this sit for now
        );
        mutateDevice(
          {
            data: {
              ...structuredClone(legacyDeviceDetails as Required<LegacyDevice>),
              phonenumber: newPhone,
              phonenumber_cost: newPhoneCost,
            },
            success: true,
          },
          { revalidate: false }
        );
        onConfirmed();
      } else {
        setError(result?.error ?? 'An unknown error occurred');
      }
    } catch (ex) {
      setError(`${ex}`);
    }

    setProcessing(false);
  }, [
    areaCode,
    countryCode,
    legacyDeviceDetails,
    mutateDevice,
    mutateSimDetails,
    onConfirmed,
    purchasePhoneNumber,
    simDetails,
  ]);

  const { reverseCountryLookup } = useCountries();

  const { billingPageUrl } = useLegacyDashboard() ?? {};
  const billingPageLink = useOrgUrl({ baseUrl: billingPageUrl ?? '', orgId: organizationId });

  const contents = useMemo(() => {
    if (isLoading) {
      return <Loader>Checking for phone numbers...</Loader>;
    }

    if (apiError) {
      // eslint-disable-next-line no-console
      console.error(`${apiError}`);
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading the SIM details header"
        >
          Please try again later
        </PanelNotification>
      );
    }

    return (
      <Form className={styles.contents}>
        <FormField label="Country" layoutDirection="row" required>
          {({ ariaDescribedbyIds, ariaLabel, primaryControlId }) => (
            <span
              id={primaryControlId}
              aria-describedby={ariaDescribedbyIds}
              aria-label={ariaLabel}
              className={styles.country}
            >
              {billing.billing_address_set ? (
                reverseCountryLookup.get(countryCode)
              ) : (
                <span
                  className={clsx(styles.empty, !billing.billing_address_set && styles.invalid)}
                >
                  —
                </span>
              )}
            </span>
          )}
        </FormField>
        {!error &&
          !phoneNumberCostError &&
          ['US', 'CA'].includes(countryCode) &&
          billing.billing_address_set && (
            <TextInputField
              label="Area code"
              layoutDirection="row"
              optional
              type="number"
              {...register('areaCode')}
            />
          )}
        <FormField
          label="Billing address"
          layoutDirection="row"
          required
          className={styles.billingAddress}
        >
          {({ ariaDescribedbyIds, ariaLabel, primaryControlId }) => (
            <div id={primaryControlId} aria-describedby={ariaDescribedbyIds} aria-label={ariaLabel}>
              {billing.billing_address_set ? (
                <BillingAddress address={billing.billing_address!} />
              ) : (
                <span
                  className={clsx(styles.empty, !billing.billing_address_set && styles.invalid)}
                >
                  —
                </span>
              )}
            </div>
          )}
        </FormField>
        <FormField label="Price per month" layoutDirection="row">
          {({ ariaDescribedbyIds, ariaLabel, primaryControlId }) => (
            <span
              id={primaryControlId}
              aria-describedby={ariaDescribedbyIds}
              aria-label={ariaLabel}
            >
              {phoneNumberCost ? (
                formatMoney(parseInt(phoneNumberCost, 10))
              ) : (
                <span className={styles.empty}>—</span>
              )}
            </span>
          )}
        </FormField>
        {balance.has_postpay !== 1 && balance.balance && (
          <FormField label="Account balance" layoutDirection="row">
            {({ ariaDescribedbyIds, ariaLabel, primaryControlId }) => (
              <div
                id={primaryControlId}
                aria-describedby={ariaDescribedbyIds}
                aria-label={ariaLabel}
              >
                {formatNumber(parseInt(balance.balance!, 10))}
              </div>
            )}
          </FormField>
        )}
      </Form>
    );
  }, [
    apiError,
    balance,
    billing,
    countryCode,
    error,
    isLoading,
    phoneNumberCost,
    phoneNumberCostError,
    register,
    reverseCountryLookup,
  ]);

  return (
    <Modal
      defaultOpen
      open={isOpen}
      setOpen={setIsOpen}
      onClosed={onCancelled}
      header="Purchase phone number"
      footerActions={
        <>
          <Button variant="secondary" onClick={() => setIsOpen(false)} disabled={processing}>
            {error ? 'Close' : 'Cancel'}
          </Button>
          {!error && (
            <Button
              onClick={onPurchasePhoneNumber}
              loading={processing}
              disabled={isPhoneNumberCostLoading || !!phoneNumberCostError}
            >
              Purchase phone number
            </Button>
          )}
        </>
      }
    >
      <div className={styles.contents}>
        {contents}
        {phoneNumberCostError &&
          phoneNumberCostError === 'No numbers available for that country' && (
            <BannerNotification variant="error">
              No phone numbers are available for the country associated with your billing address.
              If your billing address is incorrect, you can change it in{' '}
              <Link to={billingPageLink!} ariaLabel="Billing settings" type="inline">
                Billing settings
              </Link>
              . For help with international numbers, please email{' '}
              <Link href="mailto:support@hologram.io" type="inline">
                support@hologram.io
              </Link>
              .
            </BannerNotification>
          )}
        {!isBillingLoading && !billing.billing_address_set && (
          <BannerNotification variant="error">
            A billing address is required to purchase a phone number
          </BannerNotification>
        )}
        {error && !phoneNumberCost && (
          <BannerNotification variant="error">{error}</BannerNotification>
        )}
      </div>
    </Modal>
  );
};

export default PurchasePhoneNumber;
